import { useState } from 'react';
import { motion } from 'framer-motion';
import { Store, Send, Gift, Mail, Phone, MapPin, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const InviteBusinessModal = ({ isOpen, onClose, onInviteSuccess }) => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [formData, setFormData] = useState({
    businessName: '',
    businessCategory: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    reason: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.businessName.trim() || !formData.businessCategory.trim() || !formData.reason.trim()) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }

    // Validate at least one contact method
    if (!formData.contactEmail.trim() && !formData.contactPhone.trim()) {
      toast.error('Introduce al menos un email o teléfono del negocio');
      return;
    }

    // Validate email format if provided
    if (formData.contactEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.contactEmail)) {
        toast.error('El formato del email no es válido');
        return;
      }
    }

    // Validate phone format if provided
    if (formData.contactPhone.trim()) {
      const phoneRegex = /^\+?[0-9]{9,15}$/;
      const cleanPhone = formData.contactPhone.replace(/\s/g, '');
      if (!phoneRegex.test(cleanPhone)) {
        toast.error('El formato del teléfono no es válido (ej: +34612345678)');
        return;
      }
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/invitations/invite-business`,
        formData,
        {
          headers: { 'X-User-Id': user.id }
        }
      );

      toast.success(`¡${response.data.message}! 🎉`);
      
      // Reset form
      setFormData({
        businessName: '',
        businessCategory: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        reason: ''
      });
      
      if (onInviteSuccess) {
        onInviteSuccess();
      }
      
      onClose();
    } catch (error) {
      console.error('Error inviting business:', error);
      toast.error('Error al invitar negocio');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col p-0 gap-0 mx-4">
        <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-2">
          <DialogTitle className="font-manrope font-bold text-lg sm:text-xl flex items-center gap-2">
            <Store className="w-5 h-5 text-local-sage flex-shrink-0" />
            <span>Invita tu Negocio Favorito</span>
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            ¿Tu negocio local favorito no está en LokAlGrow? ¡Invítalo y gana 100 puntos!
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-4">
          <form id="invite-form" onSubmit={handleSubmit} className="space-y-4 mt-2">
            {/* Business Info Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Datos del Negocio
              </h3>
              
              <div>
                <Label htmlFor="businessName" className="text-sm">Nombre del Negocio *</Label>
                <div className="relative">
                  <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="businessName"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleChange}
                    className="pl-10 rounded-xl h-10"
                    placeholder="Ej: Café La Esquina"
                    required
                    data-testid="business-name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="businessCategory" className="text-sm">Categoría *</Label>
                <Input
                  id="businessCategory"
                  name="businessCategory"
                  value={formData.businessCategory}
                  onChange={handleChange}
                  className="rounded-xl h-10"
                  placeholder="Ej: Restaurante, Tienda, Panadería..."
                  required
                  data-testid="business-category-input"
                />
              </div>
            </div>

            {/* Contact Info Section */}
            <div className="space-y-3 pt-2 border-t">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide pt-2">
                Contacto del Negocio
              </h3>
              <p className="text-xs text-muted-foreground -mt-1">
                Para enviarle la invitación (al menos email o teléfono)
              </p>
              
              <div>
                <Label htmlFor="contactName" className="text-sm">Nombre del Contacto (opcional)</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="contactName"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    className="pl-10 rounded-xl h-10"
                    placeholder="Ej: Juan García"
                    data-testid="contact-name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="contactEmail" className="text-sm">
                  Email del Negocio *
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="contactEmail"
                    name="contactEmail"
                    type="email"
                    value={formData.contactEmail}
                    onChange={handleChange}
                    className="pl-10 rounded-xl h-10"
                    placeholder="negocio@email.com"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="contactPhone" className="text-sm">
                  Teléfono del Negocio (opcional)
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="contactPhone"
                    name="contactPhone"
                    type="tel"
                    value={formData.contactPhone}
                    onChange={handleChange}
                    className="pl-10 rounded-xl h-10"
                    placeholder="+34 612 345 678"
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>
            </div>

            {/* Recommendation Section */}
            <div className="space-y-3 pt-2 border-t">
              <div className="pt-2">
                <Label htmlFor="reason" className="text-sm">¿Por qué lo recomiendas? *</Label>
                <Textarea
                  id="reason"
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  className="rounded-xl min-h-[80px] mt-1"
                  placeholder="Ej: Mejor café de la ciudad, excelente servicio..."
                  maxLength={300}
                  required
                  data-testid="reason-textarea"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {formData.reason.length}/300 caracteres
                </p>
              </div>
            </div>

            {/* Rewards Info - Compact */}
            <div className="bg-local-sage/10 border border-local-sage/20 rounded-xl p-3">
              <div className="flex items-start gap-2">
                <Gift className="w-4 h-4 text-local-sage flex-shrink-0 mt-0.5" />
                <div className="text-xs">
                  <p className="font-semibold text-local-sage">¡Gana hasta 250 puntos!</p>
                  <p className="text-muted-foreground mt-1">
                    100 al invitar + 100 si se registran + 50 con su primer kupon
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Fixed button at bottom */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t bg-background">
          <Button
            type="submit"
            form="invite-form"
            disabled={submitting}
            className="w-full bg-local-sage hover:bg-local-sage/90 rounded-full py-5 text-sm sm:text-base"
            data-testid="submit-invitation-button"
          >
            {submitting ? (
              'Enviando invitación...'
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Invitar Negocio (Ganar 100 Puntos)
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
